/**
 * Catálogo de permissões.
 *
 * `docs/api/01-architecture.md §4.1` e `§7.1`: **permissão não é tabela**. É
 * constante versionada, consumida pelo backend (guard) e pelo frontend (esconder
 * botão). Permissão em banco significaria mudar comportamento de segurança por
 * UPDATE, sem code review, teste ou deploy.
 *
 * Cada permissão responde à PRIMEIRA das três perguntas de autorização
 * (`04-roles-permissions.md §4`): *o papel permite a ação?* O escopo
 * organizacional (unidade/equipe) e a propriedade do recurso são as perguntas 2
 * e 3, resolvidas em `escopo.ts` e no caso de uso — nunca aqui.
 *
 * Por isso um `P` da matriz da spec (permitido dentro do escopo) é `true` neste
 * arquivo: quem restringe o conjunto de registros é o filtro de escopo.
 */

export const PERMISSOES = [
  // --- Conta própria (04-roles §3.1) ---------------------------------------
  'CONTA_ALTERAR_SENHA',
  'CONTA_EDITAR_PERFIL',
  'CONTA_ENCERRAR_SESSOES',

  // --- Leads (04-roles §3.2) ----------------------------------------------
  'LEAD_LER',
  'LEAD_CRIAR',
  'LEAD_EDITAR',
  'LEAD_ASSUMIR',
  'LEAD_TRANSFERIR',
  'LEAD_TRANSFERIR_LOTE',
  'LEAD_REABRIR',
  'LEAD_EXPORTAR',
  'LEAD_IMPORTAR',
  'INTERACAO_CRIAR',
  'INTERACAO_LER_PAYLOAD_INTEGRACAO',

  // --- Clientes -----------------------------------------------------------
  'CLIENTE_LER',
  'CLIENTE_EDITAR',
  /** RN-17 / Q14: conteúdo integral das interações de outras equipes. */
  'CLIENTE_HISTORICO_INTEGRAL',
  'CLIENTE_ANONIMIZAR',
  'CLIENTE_EXPORTAR_DADOS',

  // --- Agendamentos (04-roles §3.3) ---------------------------------------
  'AGENDAMENTO_LER',
  'AGENDAMENTO_LER_DE_OUTROS',
  'AGENDAMENTO_CRIAR',
  'AGENDAMENTO_CRIAR_PARA_OUTRO',
  'AGENDAMENTO_GERENCIAR',

  // --- Propostas (04-roles §3.4 — Fase 2) ---------------------------------
  'PROPOSTA_LER',
  'PROPOSTA_CRIAR',
  'PROPOSTA_EDITAR',
  'PROPOSTA_EXCLUIR',

  // --- Usuários (04-roles §3.5) -------------------------------------------
  'USUARIO_LER',
  'USUARIO_CRIAR',
  'USUARIO_EDITAR',
  'USUARIO_ALTERAR_PAPEL',
  /** Criar ou promover ADMINISTRADOR — exclusivo do Administrador (RN-71). */
  'USUARIO_GERENCIAR_ADMINISTRADOR',
  'USUARIO_ATIVAR_DESATIVAR',
  'USUARIO_ALTERNAR_RECEBER_LEADS',
  /**
   * Definir a senha de outra pessoa. Nota ⁶ de `04-roles §3.5`: exclusivo do
   * Administrador, para que um gestor não assuma a identidade de um corretor.
   */
  'USUARIO_REDEFINIR_SENHA',
  'USUARIO_ENVIAR_CONVITE',
  'USUARIO_VINCULAR_EQUIPES',
  'USUARIO_VINCULAR_UNIDADES',
  'USUARIO_TRANSFERIR_LEADS',

  // --- Organização e catálogos (04-roles §3.6) ----------------------------
  'UNIDADE_GERENCIAR',
  'EQUIPE_GERENCIAR',
  'CATALOGO_LER',
  /** `pontos-conversao` e `produtos-associados` — só Administrador. */
  'CATALOGO_GERENCIAR_ESTRUTURAL',
  /** `motivos-*` e `tags` — Administrador e Gestor (nota ⁷, ⚠️ CONFIRMAR). */
  'CATALOGO_GERENCIAR_OPERACIONAL',
  'FORMULARIO_META_GERENCIAR',
  'WHATSAPP_GERENCIAR',

  // --- Indicadores e relatórios (04-roles §3.7) ---------------------------
  'DASHBOARD_LER',
  'PAINEL_LER',
  'RELATORIO_VENDAS_LER',
  'RELATORIO_PROPOSTAS_LER',
  'RELATORIO_EXPORTAR',
  'AVALIACAO_LER',

  // --- Auditoria e operação (04-roles §3.8) -------------------------------
  'AUDITORIA_LER',
  'OPERACAO_LER',
  'INTEGRACAO_LER_EVENTOS',
  'INTEGRACAO_REPROCESSAR',

  // --- Meta Ads (docs/release/22-meta-ads-command-center-auditoria.md §9.5) --
  /**
   * Ver métricas, hierarquia, score e diagnóstico. Leitura pura.
   *
   * Separada de `DASHBOARD_LER` de propósito: o dashboard de leads é do escopo de
   * cada atendente, e o painel de Meta Ads mostra **investimento da empresa** —
   * quanto se gasta, em quê, com que retorno. É informação de gestão, e dar isso a
   * todo papel por herança de uma permissão existente seria decidir por omissão.
   */
  'META_ADS_LER',
  /**
   * Gerar análises e recomendações, e usar o Otimizador.
   *
   * Separada da leitura porque **custa dinheiro**: cada análise narrada e cada
   * pergunta ao Otimizador é uma chamada a um provedor de IA cobrada por uso. Uma
   * permissão só faria de "abrir a tela" e "gastar por requisição" a mesma decisão.
   */
  'META_ADS_ANALISAR',
  /**
   * Cadastrar contas e **executar ações na Meta**: pausar anúncio, alterar
   * orçamento, publicar variação de criativo.
   *
   * §22 da missão: "não permitir que qualquer usuário do CRM pause anúncios ou
   * altere orçamento". Esta é a permissão que mexe em dinheiro veiculado, e a
   * matriz a dá **somente ao ADMINISTRADOR**. É também apenas a primeira de quatro
   * barreiras — as outras três são `META_ADS_ESCRITA_ATIVA` no ambiente, o
   * `confirmado: true` no corpo da requisição e o registro em
   * `meta_acoes_otimizacao` + `auditoria_log`.
   */
  'META_ADS_GERENCIAR',
] as const

export type Permissao = (typeof PERMISSOES)[number]

/** Índice para validar string vinda de fora (query param, teste, seed). */
const CONJUNTO_PERMISSOES: ReadonlySet<string> = new Set(PERMISSOES)

export function ehPermissao(valor: string): valor is Permissao {
  return CONJUNTO_PERMISSOES.has(valor)
}
